<template>
  <component
    :is="componentType"
    :to="to"
    :href="href"
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    v-bind="$attrs"
    @click="handleClick"
  >
    <span v-if="loading" class="button-loading">
      <svg class="button-spinner" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none" />
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
    </span>
    <span v-if="icon && iconPosition === 'left'" class="button-icon button-icon--left">
      <i :class="icon"></i>
    </span>
    <span class="button-text">
      <slot />
    </span>
    <span v-if="icon && iconPosition === 'right'" class="button-icon button-icon--right">
      <i :class="icon"></i>
    </span>
  </component>
</template>

<script>
export default {
  name: 'BaseButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (value) => ['primary', 'secondary', 'outline', 'ghost', 'danger'].includes(value)
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value)
    },
    type: {
      type: String,
      default: 'button'
    },
    to: {
      type: [String, Object],
      default: null
    },
    href: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    block: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: null
    },
    iconPosition: {
      type: String,
      default: 'left',
      validator: (value) => ['left', 'right'].includes(value)
    },
    rounded: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    componentType() {
      if (this.to) return 'router-link';
      if (this.href) return 'a';
      return 'button';
    },
    buttonClasses() {
      return [
        'base-button',
        `base-button--${this.variant}`,
        `base-button--${this.size}`,
        {
          'base-button--block': this.block,
          'base-button--loading': this.loading,
          'base-button--disabled': this.disabled,
          'base-button--rounded': this.rounded,
          'base-button--with-icon': this.icon
        }
      ];
    }
  },
  methods: {
    handleClick(event) {
      // デフォルトの動作を防ぐ
      event.preventDefault();
      event.stopPropagation();
      
      if (!this.disabled && !this.loading) {
        // eventオブジェクトは渡さず、単にクリックイベントをemitする
        this.$emit('click');
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
@import '@/styles/mixins.scss';

.base-button {
  @include button-base;
  position: relative;
  font-family: $font-primary;
  
  &--primary {
    @include button-variant($primary-color);
  }
  
  &--secondary {
    @include button-variant($secondary-color);
  }
  
  &--outline {
    background-color: transparent;
    border-color: $white;
    color: $white;
    
    &:hover {
      background-color: $white;
      border-color: $white;
      color: $primary-color;
    }
    
    &:active {
      background-color: darken($white, 10%);
      border-color: darken($white, 10%);
    }
    
    &:disabled {
      background-color: transparent;
      border-color: $white;
      color: $white;
    }
  }
  
  &--ghost {
    background-color: transparent;
    border-color: transparent;
    color: $primary-color;
    
    &:hover {
      background-color: rgba($primary-color, 0.1);
      color: $primary-dark;
    }
  }
  
  &--danger {
    @include button-variant($danger);
  }
  
  &--sm {
    padding: $spacing-xs $spacing-md;
    font-size: $font-size-sm;
  }
  
  &--md {
    padding: $spacing-sm $spacing-lg;
    font-size: $font-size-md;
  }
  
  &--lg {
    padding: $spacing-md $spacing-xl;
    font-size: $font-size-lg;
  }
  
  &--block {
    display: flex;
    width: 100%;
  }
  
  &--rounded {
    border-radius: $radius-full;
  }
  
  &--loading {
    color: transparent;
    
    .button-loading {
      @include absolute-center;
    }
  }
  
  &--disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  &--with-icon {
    .button-icon {
      display: inline-flex;
      align-items: center;
      
      &--left {
        margin-right: $spacing-sm;
      }
      
      &--right {
        margin-left: $spacing-sm;
      }
    }
  }
}

.button-spinner {
  animation: spin 1s linear infinite;
  width: 20px;
  height: 20px;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>